import React, { RefObject, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../../../components/_base/Button';
import Buttons from '../../../../components/button';
import { JOIN_STATUS } from '../../../../lib/constants';
import {
  isOpenMeetingRegistrationFull,
  OPEN_MEETING_FULL_MESSAGE,
  OPEN_MEETING_REGISTERING_MESSAGE,
  resolveRegisterJoinStatus,
} from '../../../../lib/helpers/openMeetingRegistrationHelper';
import Icon from '../../../../components/_base/Icon';
import useMeetingCalendar from '../../../../hooks/use-meeting-calendar';
import useEventToken from '../../../../hooks/use-event-token';
import { usePathPrefix } from '../../../../hooks/use-path-prefix';
import { normalizeRoutePath } from '../../../../lib/helpers/urlHelper';

export const JoinButton = (props: { eventId: string, meetingId: string }): JSX.Element | null => {
  const { eventId, meetingId } = props;
  const history = useHistory();
  const pathPrefix = usePathPrefix();

  return (
    <Button
      name='joinButton'
      type='button'
      onClick={() => history.push(normalizeRoutePath(`/session/${eventId}/meeting/${meetingId}`, pathPrefix), history.location.pathname)}
      label='Join'
      className='join-button'
      ariaLabel='Join Meeting'
      data-test-id='replays-join-button'
    />
  );
};

export const ViewDetailsButton = (props: { eventId: string, meetingId: string}): JSX.Element | null => {
  const { eventId, meetingId } = props;
  const history = useHistory();
  const pathPrefix = usePathPrefix();

  return (
    <Button
      name='viewDetailsButton'
      type='button'
      onClick={() => history.push(normalizeRoutePath(`/session/${eventId}/meeting/${meetingId}`, pathPrefix), history.location.pathname)}
      label='View Details'
      className='view-details-button'
      ariaLabel='View Details'
      data-test-id='replays-view-details-button'
    />
  );
};

export const MeetingFullMessage = (props: { className?: string }): JSX.Element => {
  const { className = '' } = props;
  return (
    <div
      className={`base-register base-register__disabled meeting-full-message ${className}`.trim()}
      data-test-id='replays-meeting-full-message'
      role='status'
      aria-live='polite'
    >
      {OPEN_MEETING_FULL_MESSAGE}
    </div>
  );
};

export const RegisterButton = (props: {
  joinStatus: JOIN_STATUS,
  toggleStatusOnCard: () => void,
  isLoading?: boolean,
}): JSX.Element | null => {
  const {
    joinStatus,
    toggleStatusOnCard,
    isLoading = false,
  } = props;

  if (isOpenMeetingRegistrationFull(joinStatus)) {
    return <MeetingFullMessage />;
  }

  if (!joinStatus) {
    return null;
  }

  const effectiveJoinStatus = resolveRegisterJoinStatus(joinStatus) as JOIN_STATUS;

  if (isLoading && effectiveJoinStatus === JOIN_STATUS.NOT_JOINED) {
    return (
      <div
        className='base-register register-button base-register__disabled open-meeting-registering'
        data-test-id='replays-register-loading'
        role='button'
        aria-label='Registering'
        aria-disabled='true'
      >
        <span className='preview-spinner add-spin animate-spin' />
        <label className='font-size-11px loading-label'>{OPEN_MEETING_REGISTERING_MESSAGE}</label>
      </div>
    );
  }

  if (effectiveJoinStatus === JOIN_STATUS.NOT_JOINED) {
    return (
      <div
        className='base-register register-button'
        data-test-id='replays-register-button'
        onClick={() => toggleStatusOnCard()}
        role='button'
        aria-label='Register'
      >
        <div>
          <Icon.Add className='mr-1 align-top' viewBox='0 0 24 24' />
        </div>
        <div>Register</div>
      </div>
    );
  } else if (effectiveJoinStatus === JOIN_STATUS.JOINED_AS_REGULAR) {
    return (
      <div
        className='base-register registered-button base-register__disabled'
        data-test-id='replays-registered-button'
        onClick={() => toggleStatusOnCard()}
        role='button'
        aria-label='Registered'
      >
        <div>
          <Icon.Added className='mr-1 fill-off-black align-top' viewBox='0 0 24 24' />
        </div>
        <div>Registered</div>
      </div>
    );
  } else if (effectiveJoinStatus === JOIN_STATUS.JOINED_AS_REGISTRANT) {
    return (
      <div className='register-cancel-buttons-block' >
        <div
          className='base-register registered-button'
          data-test-id='replays-registered-button'
          onClick={() => toggleStatusOnCard()}
        >
          <div>
            <Icon.Added className='mr-1 fill-off-black align-top' viewBox='0 0 24 24' />
          </div>
          <div>Registered</div>
        </div>

        <div
          className='base-register cancel-button'
          data-test-id='replays-cancel-button'
          onClick={() => toggleStatusOnCard()}
          role='button'
          aria-label='Cancel'
        >
          <div>
            <Icon.Remove className='mr-1 fill-white align-top' viewBox='1 1 18 18' height='14' width='14' />
          </div>
          <div>Cancel</div>
        </div>
      </div>
    );
  }
  return null;
};

export const AddToCalendarButton = (
  props: {
    eventId: number,
    name: string,
    isFeatured: boolean,
    meetingId: string,
    eventTitleForPassport: string
  }
): JSX.Element => {
  const { eventId, name, isFeatured, meetingId, eventTitleForPassport } = props;

  const { fetchOutlookCalendarForMeeting, fetchGoogleCalendarForMeeting } = useMeetingCalendar();
  const eventToken = useEventToken(eventId);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [customStyles, setCustomStyles] = useState<React.CSSProperties>({});

  const eventIdString = eventId.toString();
  const eventName = name || '';

  const onShowDropDown = (buttonRef: RefObject<HTMLDivElement>) => {
    const buttonLeftPosition = buttonRef.current?.getBoundingClientRect().x || 0;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (buttonLeftPosition > document.body.clientWidth / 2) {
      setCustomStyles({ left: '-214px' });
    } else {
      setCustomStyles({});
    }
  };

  return (
    <Buttons.OptionsButton
      customStyles={!isFeatured ? customStyles : {}}
      onShowDropDown={onShowDropDown}
      label='+ Add to Calendar'
      name='addToCalendar'
      disabled={false}
      eventId={eventIdString}
      eventName={eventName}
      className='select-box--button-small'
      downloadIcsFile={
        () => fetchOutlookCalendarForMeeting(
          eventIdString,
          meetingId,
          `${eventTitleForPassport || eventName}.ics`,
          eventToken
        )
      }
      openGoogleCalendar={() => fetchGoogleCalendarForMeeting(eventIdString, meetingId, eventToken)}
      iconButton
      isSlider
    />
  );
};